import { forwardRef } from 'react';
import { NoDataComponent, TableComponent } from '@/shared/ui/components';
import classNames from 'classnames';
import { useTableWrapper } from './useTableWrapper';
import type { TableWrapperProps } from './tableWrapper.types';
import './style.scss';

/**
 * Renders the waiting-to-return table together with its loading, empty and paged states. Delegates column generation, pagination state and change handling to useTableWrapper, and swaps the table for the no-data component when isEmptyData is true, passing the search flag so the empty text distinguishes a search from an empty list.
 *
 * @param props - Table props forwarded to useTableWrapper and the underlying table.
 * @param ref - Forwarded ref exposed through useTableWrapper.
 * @returns The table wrapper element, or the no-data placeholder.
 */
const TableWrapper = forwardRef((props: TableWrapperProps, ref) => {
  const { restProps, tableOuterClasses, state, generateColumns, handleTableChange, isEmptyData } =
    useTableWrapper(props, ref);

  return (
    <div
      className={classNames(
        'general-table-wrapper',
        tableOuterClasses,
        isEmptyData && 'no-data-wrapper',
      )}
    >
      {isEmptyData ? (
        <div className="no-data">
          <NoDataComponent isSearching={props.isSearching} />
        </div>
      ) : (
        <TableComponent
          {...restProps}
          columns={generateColumns(state.isLoadedData)}
          ticketCount={state.ticketCount}
          pageSize={state.sizePerPage}
          currentPage={state.page}
          data={state.data}
          isEndOfData={state.isEndOfData}
          isLoading={!state.isLoadedData}
          handleTableChange={handleTableChange}
        />
      )}
    </div>
  );
});

(TableWrapper as any).defaultProps = {
  className: undefined,
  rowKey: 'id',
  rowSelection: undefined,
  onRow: (): any => undefined,
  selectedRowKey: undefined,
  total: undefined,
  ticketCount: 0,
  isLoading: false,
  scrollX: undefined,
  tableOuterClasses: '',
  page: 1,
  sizePerPage: 10,
  fetchData: () => {},
  fetchDataCount: () => {},
  filter: {},
  isFetchCount: false,
  fetchErrorCallback: () => {},
  getPagingCursor: () => {},
  onSort: () => {},
};

export default TableWrapper;
